import Accordion from "components/common/accordion/Accordion";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import MoneyView from "components/common/views/MoneyView";
import PersonPropertyView from "components/entities/app/person/PersonPropertyView";
import { BookingViewDataQuery } from "types/graphql";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import RoomTypeView from "../../roomType/RoomTypeView";
import BookingStatusView from "./BookingStatusView";
import BookingTotalCharge from "./BookingTotalCharge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import If from "@/components/common/If";

type BookingViewModel = NonNullable<BookingViewDataQuery['booking']>;

export type BookingDataViewProps = {
    model: BookingViewModel,
    name: string
}

const BookingDataView = (props: { name: string, model: BookingViewModel }) => {
    const context = useAppContext();
    const model = props.model;
    // #region config
    const detailsDatalistConfig: DatalistPropsType<NonNullable<BookingViewDataQuery['booking']>['details']['items'][0], any, any> = {
        name: 'booking-details-page-data-list',
        dataSource: {
            data: []
        },
        defaultView: 'table',
        page: "off",
        itemId: i => i.id,
        header: false,
        table: {
            columns: [
                {
                    name: 'date',
                    title: 'fields.date.name',
                    resolve: (item) => DateUtil.format(item.date, DateFormats.SHORT),
                    columnClassName(i) {
                        return i.hasConflict ? 'text-warning' : '';
                    },
                },
                {
                    name: 'room',
                    title: 'fields.room.name',
                    resolve: (item) => item.room.name,
                    columnClassName(i) {
                        return i.hasConflict ? 'text-warning' : '';
                    },
                },
                {
                    name: 'roomType',
                    title: 'fields.roomType.name',
                    resolve: (item) => <RoomTypeView {...item.room.roomType} />,
                    columnClassName(i) {
                        return i.hasConflict ? 'text-warning' : '';
                    },
                },
                {
                    name: 'price',
                    title: 'fields.price.name',
                    resolve: (item) => <MoneyView value={item.price} />,
                    columnClassName(i) {
                        return i.hasConflict ? 'text-warning' : '';
                    },
                }
            ]
        }
    };
    // #endregion config

    const detailsView = () => {
        const details = model!.details!.items;
        if (details.length === 0) {
            return <></>;
        }

        const title: string[] = [];
        let roomId = 0;
        details.forEach((item, index) => {
            if (item.room.id !== roomId) {
                title.push(item.room.name);
                roomId = item.room.id;
            }
        });
        const config = {
            ...detailsDatalistConfig,
            dataSource: {
                data: details
            }
        }
        return <Accordion name="booking-details" title={`${context.t('fields.details.name')}: ${title.join(' - ')}`}>
            <Datalist {...config} />
        </Accordion>
    }

    if (!model) return <></>;
    var items: ItemViewPropsType['model'] = [
        {
            name: 'id',
            items: [
                {
                    name: 'id',
                    value: model.id,
                    label: 'fields.id.name'
                },
                {
                    name: 'inSeasonId',
                    value: model.inSeasonId,
                    label: 'fields.inSeasonId.name'
                },
                {
                    name: 'date',
                    label: 'fields.date.name',
                    value: `${DateUtil.format(model.inDate, DateFormats.SHORT)} - ${DateUtil.format(model.outDate, DateFormats.SHORT)}`
                },
                {
                    name: 'nights',
                    label: 'fields.nights.name',
                    value: model.nights
                },
                {
                    name: 'person',
                    label: 'fields.person.name',
                    value: <PersonPropertyView
                        personId={model.person.id}
                        name={props.name + "-person-view"}
                        personName={model.person.name}
                        contactInfo={model.person.contactInfo.items}
                        country={model.person.country}
                        countryCode={model.person.countryCode}
                        open
                        copy
                    />
                },
                {
                    name: 'roomType',
                    label: 'fields.roomType.name',
                    value: <>
                        <RoomTypeView {...model.roomType!} />
                    </>
                },
                {
                    name: 'status',
                    label: 'fields.state.name',
                    value: <>
                        <BookingStatusView
                            isCancelled={model.isCancelled}
                            hasConflict={model.hasConflict}
                            inDate={model.inDate}
                            outDate={model.outDate}
                            addText
                        />
                        <If condition={model.cancelledDate}>
                            <span className="italic text-danger"> - {DateUtil.format(model.cancelledDate!, DateFormats.SHORT)}</span>
                        </If>
                        <If condition={model.isNoShowCancellation}>
                            <span className="italic text-danger"> - {context.t('fields.isNoShowCancellation.shortName')}</span>
                        </If>
                    </>
                },
                {
                    name: 'guestCount',
                    label: 'fields.guestCount.name',
                    value: model.guestCount
                },
                {
                    name: 'children',
                    label: 'fields.children.name',
                    value: model.children
                },
                {
                    name: 'total',
                    label: 'fields.total.name',
                    value: <BookingTotalCharge total={model.total} additionalCharge={model.additionalCharge.amountSum} paid={model.paidAmount} />
                }
            ]
        },
        {
            name: 'details',
            items: [
                {
                    name: 'details',
                    value: detailsView()
                }
            ],

        },
        {
            name: 'other',
            items: [
                {
                    name: 'channel',
                    label: 'fields.channel.name',
                    value: model.channel ? (
                        <div className="flex">
                            <div>
                                <div className={model.channel.isCancelled ? 'text-neutral-500 line-through' : ''}>
                                    {model.channel.channel!.name}
                                </div>
                                <div><span>{model.channel.externalReference}</span></div>
                                <div>Tax: <MoneyView value={model.channel.total} /></div>
                            </div>
                            <div className="px-4 my-auto">
                                <a title="open" href={`https://admin.booking.com/hotel/hoteladmin/extranet_ng/manage/booking.html?res_id=${model.channel.externalReference}&hotel_id=1187704&lang=en-us&from_confirmation_email=1`} target="_blank" rel="noreferrer">
                                    <FontAwesomeIcon icon={faUpRightFromSquare} />
                                </a>
                            </div>
                        </div>
                    )
                        : `-`
                },
                {
                    name: 'season',
                    label: 'fields.season.name',
                    value: model.season.name
                },
                {
                    name: 'bookingDate',
                    label: 'fields.insertDate.name',
                    value: DateUtil.format(model.bookingDate, DateFormats.LONG),
                },
                {
                    name: 'guestReservationLinkExtension',
                    label: "accommodation.booking.guestLink",
                    value: <a className="underline" href={`https://sunrise.house/bk/${model?.guestReservationLinkExtension}`} target="_blank" rel="noreferrer">{model?.guestReservationLinkExtension}</a>,
                },
                {
                    name: 'note',
                    label: 'fields.note.name',
                    propertyType: 'note',
                    value: model.note
                }
            ]
        }
    ];

    return <ItemView model={items} view="default" name="booking-view-page-info" />;
};

export default BookingDataView;
